import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import type { SourceColumnCell } from "components/common/columns";
import {
  MaterialRequestSourceType,
  MaterialRequestSourceTypeLabel,
  type MaterialRequestSourceTypeValue,
} from "../constants/material-request.constants";
import type { MaterialRequest } from "../types/material-request.types";
export function materialRequestSourcePath(
  record: MaterialRequest,
): string | null {
  const st = record.sourceType;
  const id = record.sourceId;
  if (id == null || !Number.isFinite(Number(id))) return null;

  if (st === MaterialRequestSourceType.WORK_ORDER) {
    return `/work-orders/${id}`;
  }
  if (st === MaterialRequestSourceType.PETTY_WORK) {
    return `/petty-works/${id}`;
  }
  if (st === "ENQUIRY") {
    return `/enquiry-management/enquiries/${id}`;
  }
  if (st === "AMC") {
    return `/amc-management/${id}`;
  }
  return null;
}

export function materialRequestSourceDisplayCode(
  record: MaterialRequest,
): string {
  const st = record.sourceType;
  const id = record.sourceId;

  if (st === MaterialRequestSourceType.WORK_ORDER) {
    const c = record.workOrder?.code?.trim();
    if (c) return c;
  }
  if (st === MaterialRequestSourceType.PETTY_WORK) {
    const c = record.pettyWork?.code?.trim();
    if (c) return c;
  }
  if (st === "AMC") {
    const c = record.amc?.code?.trim();
    if (c) return c;
  }

  if (id != null && Number.isFinite(Number(id))) {
    return `#${id}`;
  }
  return "—";
}

export function materialRequestSourceLinkEl(
  record: MaterialRequest,
): ReactNode {
  const path = materialRequestSourcePath(record);
  const label = materialRequestSourceDisplayCode(record);

  if (!path) {
    if (label !== "—") {
      return <span className="text-body fw-medium">{label}</span>;
    }
    return (
      <span>
        {String(record.sourceType ?? record.source ?? "—")}
        {record.sourceId != null ? ` #${record.sourceId}` : ""}
      </span>
    );
  }

  return (
    <Link to={path} className="link fw-medium">
      {label}
    </Link>
  );
}

/** Data for {@link SourceColumn} / shared source cell layout. */
export function materialRequestSourceColumnCell(
  record: MaterialRequest,
): SourceColumnCell {
  const st = record.sourceType;
  const typeLabel = st
    ? (MaterialRequestSourceTypeLabel[st as MaterialRequestSourceTypeValue] ??
      String(st))
    : (record.source ?? "—");

  const path = materialRequestSourcePath(record);
  const label = materialRequestSourceDisplayCode(record);
  const href = path && label !== "—" ? path : null;

  return { typeLabel, primaryText: label, href };
}

/** Same layout as `SourceColumn` (type line, then code / link). */
export function materialRequestSourceStackedCell(
  record: MaterialRequest,
): ReactNode {
  const r = materialRequestSourceColumnCell(record);

  const codeLine =
    r.href != null ? (
      <Link
        to={r.href}
        className="link text-decoration-none d-block text-truncate"
        title={r.primaryText}
      >
        {r.primaryText}
      </Link>
    ) : (
      <span className="text-muted d-block text-truncate">{r.primaryText}</span>
    );

  const typeLine =
    r.typeLabel && r.typeLabel !== "—" ? (
      <div className="text-muted small text-truncate">{r.typeLabel}</div>
    ) : null;

  return (
    <div className="min-w-0 d-flex flex-column gap-1 lh-sm">
      {typeLine}
      {codeLine}
    </div>
  );
}
